import { HubConnectionState } from '@microsoft/signalr'
import { useEffect, useLayoutEffect, useRef } from 'react'

import { getConnection, startConnection, type HubEvent } from '../services/signalr'

/**
 * Subscribe to a SignalR hub event with automatic connect & cleanup.
 *
 *   useSignalR('AttendanceCheckedIn', (payload) => {
 *     queryClient.invalidateQueries({ queryKey: queryKeys.attendance.all })
 *   })
 *
 * - Connects on first subscriber, reuses the singleton for everyone else.
 * - Reads the latest `handler` via a ref so the subscription doesn't churn
 *   when the parent re-renders with a new closure.
 * - Best-effort: if the connection never opens, handlers simply never fire —
 *   the screen still works.
 */
export function useSignalR<T = unknown>(event: HubEvent, handler: (payload: T) => void) {
  const handlerRef = useRef(handler)
  // useLayoutEffect (not assignment during render) keeps `react-hooks/immutability` happy.
  useLayoutEffect(() => {
    handlerRef.current = handler
  })

  useEffect(() => {
    let cancelled = false
    let registered = false
    const conn = getConnection()
    const dispatch = (payload: T) => handlerRef.current(payload)

    const subscribe = () => {
      if (cancelled || registered) return
      conn.on(event, dispatch)
      registered = true
    }

    if (conn.state === HubConnectionState.Connected) {
      subscribe()
    } else {
      startConnection()
        .then(subscribe)
        .catch(() => {})
    }

    return () => {
      cancelled = true
      if (registered) conn.off(event, dispatch)
    }
  }, [event])
}
